import { useEffect, type RefObject } from 'react'
import type { MarketingCardTracker } from '../marketingCards'

/**
 * Impression tracking for the promo cards in the grid (#1145). One
 * IntersectionObserver per grid (WP: one per `.hfse-properties` container),
 * 0.5 threshold, observing every rendered `.hfse-marketing-card`. The
 * observer is rebuilt whenever `observeKey` changes; re-observing an
 * already-seen card is harmless because the tracker de-dupes on
 * `cardId|ordinal` for the mount.
 *
 * `observeKey` contract: it must change whenever a card NODE can be
 * remounted, not just when the slot layout changes. The grid keys its rows
 * by property id, so a filter/sort change that returns the same COUNT of
 * different listings renders brand-new card nodes at the same slots — the
 * caller folds the id of the listing each card follows into the key
 * (PropertyGrid `promoKey`), otherwise the observer keeps watching detached
 * nodes and an unseen slot never fires after the remount (#1145 review,
 * finding 3).
 *
 * No-ops during SSR and in DOMs without IntersectionObserver (happy-dom).
 */
export function useMarketingCardImpressions(
  gridRef: RefObject<HTMLElement | null>,
  observeKey: string,
  tracker: MarketingCardTracker,
): void {
  useEffect(() => {
    const grid = gridRef.current
    if (!grid || observeKey === '' || typeof IntersectionObserver === 'undefined') return

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // The initial observe() callback reports the CURRENT state even when
          // no threshold was crossed, so a card a sliver into the viewport
          // would count — gate on the ratio as well as the flag.
          if (!entry.isIntersecting || (entry.intersectionRatio ?? 0) < 0.5) continue
          const el = entry.target as HTMLElement
          const id = el.getAttribute('data-hr-mc-id') ?? ''
          const ordinal = Number(el.getAttribute('data-hr-mc-ordinal'))
          if (id !== '' && Number.isFinite(ordinal)) {
            tracker.impression(id, ordinal, el, (window as any).dataLayer)
          }
          io.unobserve(el)
        }
      },
      { threshold: 0.5 },
    )
    grid.querySelectorAll('.hfse-marketing-card').forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [gridRef, observeKey, tracker])
}
